import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { updateLayout as updateLayoutApi } from '../../../services/workspaceService';

/** Layout writes are coalesced so dragging a widget is one save, not forty. */
const SAVE_DEBOUNCE_MS = 800;

const EMPTY_LAYOUT = {
  tabs: [],
  activeTabId: null,
  defaultTabId: null,
  globalRules: [],
  globalActiveRuleIds: [],
  widgets: [],
};

/**
 * Owns the workspace's layoutConfig and the debounce in front of persisting it.
 *
 * Every change goes through updateLayoutAndPersist, so the local state and the
 * pending write can never disagree about which version is being saved.
 */
export default function useWorkspaceLayout(workspaceId, trackSave) {
  const [layoutConfig, setLayoutConfig] = useState(EMPTY_LAYOUT);
  const saveTimer = useRef(null);
  const pending = useRef(null);

  /** Write whatever is waiting, now. */
  const flush = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const payload = pending.current;
    if (payload === null) return;
    pending.current = null;
    trackSave(updateLayoutApi(workspaceId, payload)).catch(() => toast.error('Failed to save layout'));
  }, [workspaceId, trackSave]);

  const persist = useCallback((newLayoutConfig) => {
    pending.current = newLayoutConfig;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush]);

  const updateLayoutAndPersist = useCallback((newLayoutConfig) => {
    setLayoutConfig(newLayoutConfig);
    persist(newLayoutConfig);
  }, [persist]);

  // Leaving the workspace inside the debounce window used to drop the edit: the
  // timer was only ever cleared by the next change, and nothing ran it early.
  // (A tab close still races the request — the browser gives no guarantee once
  // unload starts — but navigating away no longer loses work.)
  useEffect(() => flush, [flush]);

  return { layoutConfig, setLayoutConfig, updateLayoutAndPersist };
}
